import { useState } from "react";
import { Link } from "react-router-dom";
import { usePagesDB } from "@/hooks/usePagesDB";
import { type StaticPage, type PageSection } from "@/mocks/admin";
import AdminLayout from "@/components/feature/AdminLayout";
import MobileTableCards from "@/components/feature/MobileTableCards";
import StaticPageRenderer from "@/components/feature/StaticPageRenderer";

const sectionTypeLabels: Record<PageSection["type"], string> = {
  text: "Text",
  heading: "Überschrift",
  quote: "Zitat",
  list: "Liste",
  contact_form: "Kontaktformular",
  image: "Bild",
  cta: "Call-to-Action",
};

const sectionTypeIcons: Record<PageSection["type"], string> = {
  text: "ri-text",
  heading: "ri-heading",
  quote: "ri-double-quotes-l",
  list: "ri-list-check",
  contact_form: "ri-mail-line",
  image: "ri-image-line",
  cta: "ri-megaphone-line",
};

/* ─── Live Preview (right panel in editor) ─── */
function PagePreview({
  title,
  content,
  sections,
}: {
  title: string;
  content: string;
  sections: PageSection[];
}) {
  const previewPage: StaticPage = {
    id: "preview",
    title: title || "Vorschau",
    slug: "preview",
    content,
    metaTitle: "",
    metaDescription: "",
    isPublished: true,
    lastEdited: new Date().toISOString(),
    sections: sections.length > 0 ? sections : undefined,
  };

  return <StaticPageRenderer page={previewPage} preview />;
}

/* ─── Mobile Page Card ─── */
function MobilePageCard({ page }: { page: StaticPage }) {
  return (
    <div className="flex gap-3">
      <div className="w-10 h-10 rounded-lg bg-hj-gray-light flex items-center justify-center shrink-0">
        <i className="ri-file-text-line text-hj-gray text-lg"></i>
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-semibold text-hj-dark font-body">{page.title}</h3>
        <p className="text-[10px] text-hj-gray font-mono mt-0.5">/{page.slug}</p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full ${
            page.isPublished ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
          }`}>
            {page.isPublished ? "Veröffentlicht" : "Entwurf"}
          </span>
          <span className="text-[10px] text-hj-gray">
            {new Date(page.lastEdited).toLocaleDateString("de-CH")}
          </span>
          {page.sections && (
            <span className="text-[10px] text-hj-gray">{page.sections.length} Abschnitte</span>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Mobile Page Actions ─── */
function MobilePageActions({
  page,
  onEdit,
  onDelete,
}: {
  page: StaticPage;
  onEdit: (page: StaticPage) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <>
      <Link
        to={`/${page.slug}`}
        target="_blank"
        className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-hj-gray bg-hj-gray-light rounded-md hover:bg-hj-gray-border transition-colors"
      >
        <i className="ri-eye-line"></i>
        Vorschau
      </Link>
      <button
        onClick={() => onEdit(page)}
        className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-hj-red border border-hj-red rounded-md hover:bg-hj-red hover:text-white transition-colors"
      >
        <i className="ri-edit-line"></i>
        Bearbeiten
      </button>
      <button
        onClick={() => onDelete(page.id)}
        className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 transition-colors"
      >
        <i className="ri-delete-bin-line"></i>
        Löschen
      </button>
    </>
  );
}

export default function AdminPages() {
  const { pages: dbPages, loading, error, refetch, createPage, updatePage, deletePage } = usePagesDB();
  const [search, setSearch] = useState("");
  const [showEditor, setShowEditor] = useState(false);
  const [editingPage, setEditingPage] = useState<StaticPage | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Editor form state
  const [formTitle, setFormTitle] = useState("");
  const [formSlug, setFormSlug] = useState("");
  const [formContent, setFormContent] = useState("");
  const [formMetaTitle, setFormMetaTitle] = useState("");
  const [formMetaDesc, setFormMetaDesc] = useState("");
  const [formPublished, setFormPublished] = useState(true);
  const [formSections, setFormSections] = useState<PageSection[]>([]);

  const filtered = dbPages.filter(
    (p) =>
      p.title.toLowerCase().includes(search.toLowerCase()) ||
      p.slug.toLowerCase().includes(search.toLowerCase())
  );

  const openEditor = (page?: StaticPage) => {
    if (page) {
      setEditingPage(page);
      setFormTitle(page.title);
      setFormSlug(page.slug);
      setFormContent(page.content);
      setFormMetaTitle(page.metaTitle);
      setFormMetaDesc(page.metaDescription);
      setFormPublished(page.isPublished);
      setFormSections(page.sections || []);
    } else {
      setEditingPage(null);
      setFormTitle("");
      setFormSlug("");
      setFormContent("");
      setFormMetaTitle("");
      setFormMetaDesc("");
      setFormPublished(true);
      setFormSections([]);
    }
    setShowEditor(true);
  };

  const closeEditor = () => {
    setShowEditor(false);
    setEditingPage(null);
  };

  const handleSave = async () => {
    if (!formTitle || !formSlug) return;
    setSaving(true);

    const payload = {
      title: formTitle,
      slug: formSlug,
      content: formContent,
      metaTitle: formMetaTitle,
      metaDescription: formMetaDesc,
      isPublished: formPublished,
      sections: formSections,
    };

    if (editingPage) {
      await updatePage(editingPage.id, payload);
    } else {
      await createPage(payload as Omit<StaticPage, "id" | "lastEdited">);
    }

    setSaving(false);
    closeEditor();
  };

  const handleDelete = (id: string) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (deleteId) {
      await deletePage(deleteId);
    }
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const addSection = (type: PageSection["type"]) => {
    const newSection: PageSection = {
      id: `s-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      type,
      content: "",
      title: type === "heading" ? "Neue Überschrift" : undefined,
    };
    setFormSections((prev) => [...prev, newSection]);
  };

  const updateSection = (id: string, updates: Partial<PageSection>) => {
    setFormSections((prev) =>
      prev.map((s) => (s.id === id ? { ...s, ...updates } : s))
    );
  };

  const removeSection = (id: string) => {
    setFormSections((prev) => prev.filter((s) => s.id !== id));
  };

  const moveSection = (id: string, direction: "up" | "down") => {
    setFormSections((prev) => {
      const idx = prev.findIndex((s) => s.id === id);
      if (idx === -1) return prev;
      const newIdx = direction === "up" ? idx - 1 : idx + 1;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      return next;
    });
  };

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5 md:mb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-hj-dark font-heading">Seiten</h1>
            <p className="text-sm text-hj-gray mt-1 font-body">{dbPages.length} Seiten insgesamt</p>
          </div>
          <button
            onClick={() => openEditor()}
            className="px-4 py-2.5 bg-hj-red text-white text-sm font-medium rounded-md hover:bg-hj-red-light transition-colors flex items-center gap-2 whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Neue Seite
          </button>
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg border border-hj-gray-border p-3 md:p-4 mb-4 md:mb-6">
          <div className="relative max-w-md">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-hj-gray"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Seiten suchen..."
              className="w-full pl-9 pr-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red font-body"
            />
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-hj-red border-t-transparent rounded-full animate-spin"></div>
            <span className="ml-3 text-sm text-hj-gray font-body">Seiten laden...</span>
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 flex items-center gap-3">
            <i className="ri-alert-line text-amber-600"></i>
            <p className="text-sm text-amber-700 font-body">
              Supabase nicht verbunden — Änderungen werden lokal gespeichert. {error}
            </p>
            <button
              onClick={() => refetch()}
              className="ml-auto text-xs font-medium text-amber-700 hover:underline"
            >
              Erneut versuchen
            </button>
          </div>
        )}

        {/* Mobile Card View */}
        {!loading && (
          <div className="lg:hidden mb-4">
            <MobileTableCards
              items={filtered}
              keyExtractor={(p) => p.id}
              renderCard={(page) => <MobilePageCard page={page} />}
              renderActions={(page) => (
                <MobilePageActions
                  page={page}
                  onEdit={openEditor}
                  onDelete={handleDelete}
                />
              )}
              emptyMessage="Keine Seiten gefunden."
              onSwipeLeft={(page) => handleDelete(page.id)}
              swipeLeftLabel="Löschen"
            />
          </div>
        )}

        {/* Desktop Table */}
        {!loading && (
          <div className="hidden lg:block bg-white rounded-lg border border-hj-gray-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-hj-gray-light">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-hj-gray uppercase tracking-wider font-body">Seite</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-hj-gray uppercase tracking-wider font-body">Slug</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-hj-gray uppercase tracking-wider font-body">Meta-Titel</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-hj-gray uppercase tracking-wider font-body">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-hj-gray uppercase tracking-wider font-body">Abschnitte</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-hj-gray uppercase tracking-wider font-body">Bearbeitet</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-hj-gray uppercase tracking-wider font-body">Aktionen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-hj-gray-border">
                  {filtered.map((page) => (
                    <tr key={page.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-hj-dark font-body">{page.title}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-hj-gray font-mono">/{page.slug}</td>
                      <td className="px-4 py-3 text-sm text-hj-gray truncate max-w-[200px] font-body">{page.metaTitle}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${
                          page.isPublished ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                        }`}>
                          {page.isPublished ? "Veröffentlicht" : "Entwurf"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-hj-gray font-body">
                        {page.sections?.length || 0}
                      </td>
                      <td className="px-4 py-3 text-sm text-hj-gray font-body">
                        {new Date(page.lastEdited).toLocaleDateString("de-CH")}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Link
                            to={`/${page.slug}`}
                            target="_blank"
                            className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-hj-gray-light transition-colors"
                            title="Vorschau"
                          >
                            <i className="ri-eye-line text-hj-gray"></i>
                          </Link>
                          <button
                            onClick={() => openEditor(page)}
                            className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-hj-gray-light transition-colors cursor-pointer"
                            title="Bearbeiten"
                          >
                            <i className="ri-edit-line text-hj-gray"></i>
                          </button>
                          <button
                            onClick={() => handleDelete(page.id)}
                            className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-red-50 transition-colors cursor-pointer"
                            title="Löschen"
                          >
                            <i className="ri-delete-bin-line text-red-500"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filtered.length === 0 && (
              <div className="text-center py-12">
                <i className="ri-file-text-line text-4xl text-hj-gray-border mb-3 block"></i>
                <p className="text-hj-gray font-body">Keine Seiten gefunden.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Editor Modal */}
      {showEditor && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto">
          <div className="bg-white rounded-lg w-full max-w-6xl mx-4 my-6 flex flex-col lg:flex-row overflow-hidden" style={{ height: "calc(100vh - 48px)" }}>
            {/* ── LEFT: Form ── */}
            <div className="flex-1 flex flex-col min-w-0 max-h-[calc(100vh-48px)]">
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-hj-gray-border shrink-0 bg-white">
                <h2 className="text-lg font-bold text-hj-dark font-heading">
                  {editingPage ? "Seite bearbeiten" : "Neue Seite"}
                </h2>
                <button
                  onClick={closeEditor}
                  className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-hj-gray-light transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-lg text-hj-dark"></i>
                </button>
              </div>

              {/* Scrollable form */}
              <div className="flex-1 overflow-y-auto p-5 space-y-5">
                {/* Basic info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-hj-dark mb-1.5 font-body">Titel</label>
                    <input
                      type="text"
                      value={formTitle}
                      onChange={(e) => setFormTitle(e.target.value)}
                      className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red font-body"
                      placeholder="Seitentitel"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-hj-dark mb-1.5 font-body">Slug</label>
                    <input
                      type="text"
                      value={formSlug}
                      onChange={(e) => setFormSlug(e.target.value)}
                      className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red font-body"
                      placeholder="seiten-url"
                    />
                  </div>
                </div>

                {/* Meta */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-hj-dark mb-1.5 font-body">Meta-Titel</label>
                    <input
                      type="text"
                      value={formMetaTitle}
                      onChange={(e) => setFormMetaTitle(e.target.value)}
                      className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red font-body"
                      placeholder="SEO-Titel"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-hj-dark mb-1.5 font-body">Meta-Beschreibung</label>
                    <input
                      type="text"
                      value={formMetaDesc}
                      onChange={(e) => setFormMetaDesc(e.target.value)}
                      className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red font-body"
                      placeholder="SEO-Beschreibung"
                    />
                  </div>
                </div>

                {/* Published toggle */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setFormPublished(!formPublished)}
                    className={`relative w-12 h-7 rounded-full transition-colors cursor-pointer ${
                      formPublished ? "bg-emerald-500" : "bg-hj-gray-border"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${
                        formPublished ? "translate-x-5" : "translate-x-0.5"
                      }`}
                    />
                  </button>
                  <span className="text-sm font-medium text-hj-dark font-body">
                    {formPublished ? "Veröffentlicht" : "Entwurf"}
                  </span>
                </div>

                {/* Main content */}
                <div>
                  <label className="block text-sm font-medium text-hj-dark mb-1.5 font-body">Einleitungstext</label>
                  <textarea
                    value={formContent}
                    onChange={(e) => setFormContent(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red font-body resize-none"
                    placeholder="Hauptinhalt der Seite..."
                  />
                </div>

                {/* Sections */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-hj-dark font-body">Abschnitte</label>
                    <span className="text-xs text-hj-gray font-body">{formSections.length} Abschnitte</span>
                  </div>

                  <div className="space-y-3">
                    {formSections.map((section, idx) => (
                      <div key={section.id} className="border border-hj-gray-border rounded-lg p-3 bg-hj-gray-light/30">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <i className={`${sectionTypeIcons[section.type]} text-hj-red text-sm`}></i>
                            <span className="text-xs font-medium text-hj-dark font-body">
                              {idx + 1}. {sectionTypeLabels[section.type]}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => moveSection(section.id, "up")}
                              disabled={idx === 0}
                              className="w-7 h-7 flex items-center justify-center rounded hover:bg-hj-gray-light transition-colors disabled:opacity-30 cursor-pointer"
                            >
                              <i className="ri-arrow-up-line text-xs"></i>
                            </button>
                            <button
                              onClick={() => moveSection(section.id, "down")}
                              disabled={idx === formSections.length - 1}
                              className="w-7 h-7 flex items-center justify-center rounded hover:bg-hj-gray-light transition-colors disabled:opacity-30 cursor-pointer"
                            >
                              <i className="ri-arrow-down-line text-xs"></i>
                            </button>
                            <button
                              onClick={() => removeSection(section.id)}
                              className="w-7 h-7 flex items-center justify-center rounded hover:bg-red-50 transition-colors cursor-pointer"
                            >
                              <i className="ri-delete-bin-line text-xs text-red-500"></i>
                            </button>
                          </div>
                        </div>

                        {section.type === "heading" && (
                          <input
                            type="text"
                            value={section.content}
                            onChange={(e) => updateSection(section.id, { content: e.target.value })}
                            className="w-full px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red font-body"
                            placeholder="Überschrift"
                          />
                        )}

                        {(section.type === "text" || section.type === "quote") && (
                          <textarea
                            value={section.content}
                            onChange={(e) => updateSection(section.id, { content: e.target.value })}
                            rows={3}
                            className="w-full px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red font-body resize-none"
                            placeholder={section.type === "quote" ? "Zitat..." : "Text..."}
                          />
                        )}

                        {section.type === "list" && (
                          <textarea
                            value={section.content}
                            onChange={(e) => updateSection(section.id, { content: e.target.value })}
                            rows={3}
                            className="w-full px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red font-body resize-none"
                            placeholder="Liste (mit | trennen)"
                          />
                        )}

                        {section.type === "cta" && (
                          <div className="space-y-2">
                            <input
                              type="text"
                              value={section.title || ""}
                              onChange={(e) => updateSection(section.id, { title: e.target.value })}
                              className="w-full px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red font-body"
                              placeholder="CTA-Titel"
                            />
                            <input
                              type="text"
                              value={section.content}
                              onChange={(e) => updateSection(section.id, { content: e.target.value })}
                              className="w-full px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red font-body"
                              placeholder="CTA-Link oder Text"
                            />
                          </div>
                        )}

                        {section.type === "image" && (
                          <input
                            type="text"
                            value={section.content}
                            onChange={(e) => updateSection(section.id, { content: e.target.value })}
                            className="w-full px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red font-body"
                            placeholder="Bild-URL"
                          />
                        )}

                        {section.type === "contact_form" && (
                          <p className="text-xs text-hj-gray font-body">
                            Kontaktformular wird automatisch auf der Seite gerendert.
                          </p>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {(["heading", "text", "quote", "list", "cta", "image", "contact_form"] as PageSection["type"][]).map(
                      (type) => (
                        <button
                          key={type}
                          onClick={() => addSection(type)}
                          className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-hj-dark bg-hj-gray-light rounded-md hover:bg-hj-gray-border transition-colors cursor-pointer"
                        >
                          <i className={`${sectionTypeIcons[type]} text-sm`}></i>
                          {sectionTypeLabels[type]}
                        </button>
                      )
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-hj-gray-border shrink-0 bg-white">
                <button
                  onClick={closeEditor}
                  className="px-4 py-2.5 text-sm font-medium text-hj-gray border border-hj-gray-border rounded-md hover:bg-gray-50 transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleSave}
                  disabled={!formTitle || !formSlug || saving}
                  className="px-4 py-2.5 text-sm font-medium text-white bg-hj-red rounded-md hover:bg-hj-red-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {saving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                  <i className="ri-save-line"></i>
                  {saving ? "Speichern..." : "Speichern"}
                </button>
              </div>
            </div>

            {/* ── RIGHT: Live Preview ── */}
            <div className="hidden lg:flex flex-col w-[420px] xl:w-[480px] border-l border-hj-gray-border bg-hj-gray-light/20 shrink-0">
              <div className="px-4 py-3 border-b border-hj-gray-border bg-white flex items-center justify-between shrink-0">
                <span className="text-xs font-semibold text-hj-dark uppercase tracking-wider font-body">Live-Vorschau</span>
                <span className="text-[11px] text-hj-muted font-body">Änderungen sofort sichtbar</span>
              </div>
              <div className="flex-1 overflow-y-auto">
                <PagePreview
                  title={formTitle}
                  content={formContent}
                  sections={formSections}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-lg p-5 md:p-6 max-w-sm w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <i className="ri-alert-line text-red-500 text-lg"></i>
              </div>
              <h3 className="text-lg font-bold text-hj-dark font-heading">Seite löschen?</h3>
            </div>
            <p className="text-sm text-hj-gray mb-6 font-body">
              Diese Aktion kann nicht rückgängig gemacht werden. Die Seite wird dauerhaft entfernt.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-hj-gray border border-hj-gray-border rounded-md hover:bg-gray-50 transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-500 rounded-md hover:bg-red-600 transition-colors"
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}